import uuid from 'uuid';
import { SettingsApiService } from '../settings/settings-api-service';
import i18n from '../../../../i18n';
import { ProductPermission } from '../../../enum/product-permission';
import { TenantCodes } from '../../../enum/tenant';
import { IProduct, ISettingsKeystoreEntry } from '../../domain';
import { ILanguage } from '../../domain/base/language';
import { parseToSettings } from '../../../utils/map-settings';
import mockKeystoreEntriesJson from './mock-keystore-entries.json';
import { DefaultProductCategory } from '../../../enum/product-category';
import { CardArts } from '../../../utils/product-helpers';
import { mockImagesMap } from './mock-images-map';

const mockKeystoreEntries = mockKeystoreEntriesJson as ISettingsKeystoreEntry[];

const languages: ILanguage[] = [
    {
        languageCode: 'de',
        active: true,
    },
    {
        languageCode: 'en',
        active: true,
    },
    {
        languageCode: 'fr',
        active: true,
    },
    {
        languageCode: 'it',
        active: true,
    },
];

const mockSettings = parseToSettings(mockKeystoreEntries);

const allCategories = Object.keys(DefaultProductCategory).concat(mockSettings.customProductCategories.map((c) => c.categoryId));

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const permissions = Object.keys(ProductPermission).map((key) => ProductPermission[key]);
export const products = allCategories
    .map((categoryId) =>
        permissions.map((permission) =>
            Array(6)
                .fill('')
                .map(
                    (_, idx) =>
                        ({
                            title: [
                                {
                                    languageCode: 'de',
                                    translation:
                                        categoryId === DefaultProductCategory.CARDS
                                            ? `${idx % 3 === 0 ? 'Debit Card' : 'Credit Card'} ${idx}`
                                            : `${categoryId}`,
                                },
                            ],
                            permissions: [permission],
                            category: categoryId,
                            priority: idx,
                            productId: uuid.v4(),
                            finnovaId: `${idx % 3 === 0 ? 2 : 400}`,
                            abbreviation: [
                                {
                                    languageCode: 'de',
                                    translation: `Beschreibung von Produkt ${i18n.t(
                                        `products.category.${categoryId.toLowerCase()}`,
                                    )} ${idx}, perfekt für den Zahlungsverkehr sowie für Karten- und Wertschriftentransaktionen.`,
                                },
                            ],
                            link: [
                                {
                                    translation: `Weitere Informationen`,
                                    languageCode: 'de',
                                    url: `/produkt-${idx}`,
                                },
                            ],
                            enableEditCategory: true,
                            ...(categoryId === DefaultProductCategory.CARDS
                                ? {
                                      cardDailyLimitDefault: 2100,
                                      cardDailyLimitMax: 7500,
                                      cardMonthlyLimitDefault: 2800,
                                      cardMonthlyLimitMax: 12500,
                                      admissibleAccountTypes: idx % 2 === 0 ? [2, 4, 8, 15, 67] : undefined,
                                      enableEditCategory: true,
                                  }
                                : {}),
                        } as IProduct),
                ),
        ),
    )
    .flat(2)
    // add a default card product - this card type is used for the debit card option on payment ("Zahlen") products
    // good to have such a card in mock data for testing of card orders
    .concat([
        {
            title: [
                {
                    languageCode: 'de',
                    translation: 'Default Debit Card 930',
                },
            ],
            permissions: permissions,
            category: DefaultProductCategory.CARDS,
            priority: 50,
            productId: uuid.v4(),
            finnovaId: `${CardArts.DEBIT_MASTERCARD}`,
            abbreviation: [
                {
                    languageCode: 'de',
                    translation: `Beschreibung von Default Debit Card, die beste Default Card überhaupt`,
                },
            ],
            link: [
                {
                    translation: `Weitere Informationen`,
                    languageCode: 'de',
                    url: `/produkt-card-default`,
                },
            ],
            cardDailyLimitDefault: 1400,
            cardDailyLimitMax: 7500,
            cardMonthlyLimitDefault: 3800,
            cardMonthlyLimitMax: 12500,
            admissibleAccountTypes: [2],
            enableEditCategory: true,
        },
    ]);

export class MockSettingsApiService extends SettingsApiService {
    permission: ProductPermission;

    constructor(code?: TenantCodes, permission?: ProductPermission) {
        super();
        if (!code) {
            console.error('No tenant code provided in MockSettingsApiService');
        }
        this.getContextHeaders = async () => ({ 'x-tnt': code, 'x-sec-iam-api-url': 'https://wwwsec.bank-xray.dev.esprit.ch/authen/rest/public' });
        this.permission = permission || ProductPermission.ANONYMOUS;
    }

    async getLanguages(): Promise<ILanguage[]> {
        return languages;
    }

    async getProducts(categoryIds: string[] = mockSettings.customProductCategories.map((key) => key.categoryId)): Promise<IProduct[]> {
        await sleep(250);
        return products.filter((product) => product.permissions.includes(this.permission) && categoryIds.includes(product.category));
    }

    async getKeystoreEntries(): Promise<ISettingsKeystoreEntry[]> {
        await sleep(50);

        return mockKeystoreEntries;
    }
}

export const getImageUrl = (id: string): string => mockImagesMap[id];
